import type { ReactNode } from 'react';

export type RowValueTone = 'default' | 'pos' | 'neg';

export interface RowProps {
  /** Primary label (first line). */
  readonly label: ReactNode;
  /** Optional secondary line below the label (mono, smaller). */
  readonly meta?: ReactNode;
  /** Right-aligned value. */
  readonly value?: ReactNode;
  /** Color tone for the value. Used in History rows for signed amounts. */
  readonly valueTone?: RowValueTone;
  /** Optional onClick — when present, the row gains hover + cursor. */
  readonly onClick?: () => void;
  readonly className?: string;
}

const VALUE_TONE_CLASS: Record<RowValueTone, string> = {
  default: 'text-text-primary',
  pos: 'text-pos',
  neg: 'text-neg',
};

/**
 * Standard list row primitive used inside ListCard.
 *
 * Layout: 2-column grid (1fr | auto) with label/meta on the left,
 * value on the right. Adjacent rows get a hairline divider via the
 * parent ListCard's `[&>*+*]:border-t` selector.
 *
 * `valueTone` recolours the right-aligned value for signed amounts
 * (History: +received / −sent).
 */
export function Row({
  label,
  meta,
  value,
  valueTone = 'default',
  onClick,
  className = '',
}: RowProps) {
  const interactive = onClick !== undefined;
  return (
    <div
      className={[
        'grid grid-cols-[1fr_auto] items-center gap-3 py-[11px]',
        interactive &&
          'cursor-pointer transition-colors duration-fast hover:bg-surface-2/40 active:scale-[0.99]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div>
        <div className="text-[13px] font-medium tracking-[-0.2px] text-text-primary">
          {label}
        </div>
        {meta !== undefined && (
          <div className="mt-0.5 font-mono text-[10px] text-text-tertiary">{meta}</div>
        )}
      </div>
      {value !== undefined && (
        <div
          data-row-value
          className={[
            'text-right text-[13px] font-medium',
            VALUE_TONE_CLASS[valueTone],
          ].join(' ')}
        >
          {value}
        </div>
      )}
    </div>
  );
}
